import React, { useState, useEffect } from 'react';
import { MapPin, Clock, BellRing, AlertCircle, Check, X, User, Settings, ArrowRight, Phone, MessageSquare } from 'lucide-react';

const ServiceDashboard = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [status, setStatus] = useState('available');
  const [newRequests, setNewRequests] = useState([]);

  // Simulate incoming requests
  useEffect(() => {
    const timer = setInterval(() => {
      if (Math.random() > 0.7) {
        const newRequest = {
          id: Date.now(),
          name: `Client ${Math.floor(Math.random() * 100)}`,
          location: "Strada Principală 123",
          distance: `${(Math.random() * 5).toFixed(1)} km`,
          problem: "Pană de cauciuc",
          carModel: "Volkswagen Golf",
          timestamp: new Date().toISOString(),
          status: 'new',
          isNew: true
        };
        setNewRequests(prev => [newRequest, ...prev]);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const ServiceHeader = () => (
    <div className="bg-white border-b p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard Vulcanizare Mobilă</h1>
          <p className="text-gray-600 mt-1">Gestionare cereri și servicii</p>
        </div>
        <div className="flex items-center gap-4">
          <button className="p-2 relative">
            <BellRing className="w-6 h-6 text-gray-600" />
            {newRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center">
                {newRequests.length}
              </span>
            )}
          </button>
          <button className="p-2">
            <Settings className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Status Toggle */}
      <div className="mt-4 flex items-center gap-4">
        <span className="text-gray-700">Status:</span>
        <button
          onClick={() => setStatus(status === 'available' ? 'busy' : 'available')}
          className={`px-4 py-2 rounded-full font-medium flex items-center gap-2 ${
            status === 'available' 
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${
            status === 'available' ? 'bg-green-500' : 'bg-red-500'
          }`} />
          {status === 'available' ? 'Disponibil' : 'Ocupat'}
        </button>
      </div>
    </div>
  );

  const NewRequestAlert = ({ request }) => (
    <div className={`bg-white rounded-lg shadow-lg p-4 mb-4 border-l-4 border-blue-500 ${
      request.isNew ? 'animate-pulse' : ''
    }`}>
      <div className="flex justify-between items-start">
        <div className="flex gap-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <AlertCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{request.name}</h3>
            <div className="flex items-center gap-2 text-gray-600 mt-1">
              <MapPin className="w-4 h-4" />
              <span>{request.location}</span>
              <span className="text-sm">({request.distance})</span>
            </div>
            <div className="mt-2">
              <p className="text-gray-700">
                <span className="font-medium">Mașină:</span> {request.carModel}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Problemă:</span> {request.problem}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button className="p-2 bg-green-100 rounded-lg hover:bg-green-200"
                  onClick={() => setNewRequests(prev => prev.filter(r => r.id !== request.id))}>
            <Check className="w-5 h-5 text-green-600" />
          </button>
          <button className="p-2 bg-red-100 rounded-lg hover:bg-red-200"
                  onClick={() => setNewRequests(prev => prev.filter(r => r.id !== request.id))}>
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
          <Phone className="w-4 h-4" />
          Contactează
        </button>
        <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 flex items-center justify-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Trimite Mesaj
        </button>
      </div>
    </div>
  );

  const WaitingState = () => (
    <div className="text-center py-12">
      <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
        <Clock className="w-12 h-12 text-gray-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Așteptare Cereri
      </h2>
      <p className="text-gray-600">
        Momentan nu sunt cereri noi de la clienți.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <ServiceHeader />
      
      <div className="p-4">
        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'requests'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600'
            }`}
          >
            Cereri Noi ({newRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'active'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600'
            }`}
          >
            Servicii Active
          </button>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto">
          {activeTab === 'requests' && (
            <>
              {newRequests.length > 0 ? (
                <div className="space-y-4">
                  {newRequests.map(request => (
                    <NewRequestAlert key={request.id} request={request} />
                  ))}
                </div>
              ) : (
                <WaitingState />
              )}
            </>
          )}

          {activeTab === 'active' && (
            <div className="bg-white rounded-lg p-4">
              <h3 className="text-lg font-medium mb-4">Servicii Active</h3>
              {/* Add active services content here */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDashboard;